import React from "react";
import { Button, Modal, Font } from "@moneyforward/mfx_biz_react_components";
import { css } from "@emotion/react";
import { Billing } from "@/generated/graphql_billing";
import { color } from "@/styles/theme";
import { FormProvider, useForm } from "react-hook-form";
import { InvoiceRecipientNameFormField } from "@/components/molecules/InvoiceRecipientNameFormField";
import { UsingMonthFormField } from "@/components/molecules/UsingMonthFormField";
import { useRouter } from "next/router";
import { getUrlWithQuery } from "@/libs/url";

type Props = {
  active: boolean;
  issuableBillings: Billing[];
  companyName: string;
  onClickCancel: () => void;
};

type FormValues = {
  usingMonth: string;
  invoiceRecipientName: string;
};

export const IssueInvoiceModal = ({
  active,
  issuableBillings,
  companyName,
  onClickCancel,
}: Props) => {
  const router = useRouter();
  const { app } = router.query;
  const usingMonths = issuableBillings
    .map((bill) => bill.usingMonth)
    .sort((a, b) => {
      return a > b ? -1 : 1;
    });

  const methods = useForm<FormValues>({
    reValidateMode: "onBlur",
    defaultValues: {
      usingMonth: usingMonths[0],
      invoiceRecipientName: companyName,
    },
  });
  const { handleSubmit, formState } = methods;

  const onSubmit = async (formValues: FormValues) => {
    router.push(
      getUrlWithQuery("/issue-invoice", app, {
        using_month: formValues.usingMonth,
        invoice_recipient_name: formValues.invoiceRecipientName,
      })
    );
  };

  const body = (
    <div css={container}>
      <Font>
        請求書を発行したい月を選択してください。
        <br />
        「発行」ボタンを押すと別画面にて請求書が表示されます。
      </Font>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div css={inputWrapper}>
            <UsingMonthFormField
              name={"usingMonth"}
              labelRequired={true}
              usingMonths={usingMonths}
            ></UsingMonthFormField>
            <InvoiceRecipientNameFormField name={"invoiceRecipientName"} />
          </div>
          <div css={buttonWrapper}>
            <Button
              type="button"
              variant="secondary"
              disabled={formState.isSubmitting}
              onClick={onClickCancel}
            >
              キャンセル
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={formState.isSubmitting}
            >
              発行
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );

  if (!active) {
    return <></>;
  }

  return (
    <Modal
      title="請求書の発行"
      body={body}
      zIndex={20}
      onClose={onClickCancel}
      backgroundColor={color.backgroundGray}
      css={modal}
    />
  );
};

const modal = css({
  padding: "24px",
});

const container = css({
  marginTop: "16px",
});

const inputWrapper = css({
  marginTop: "16px",
  display: "flex",
  flexDirection: "column",
  gap: "32px",
});

const buttonWrapper = css({
  marginTop: "24px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  gap: "16px",
});
